import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

type RangeItem = number | 'ellipsis';

// Mirrors the prototype's `pagination._getPageRange` (components.js):
// always shows first/last; collapses middle to ellipses outside a sliding window.
function buildRange(current: number, total: number): RangeItem[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: RangeItem[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push('ellipsis');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('ellipsis');

  pages.push(total);
  return pages;
}

export function Pagination({ page, totalPages, onPageChange, className }: PaginationProps) {
  // Prototype parity (components.js pagination.render): show pagination whenever
  // ≥1 page exists — a single page renders "1" with disabled arrows. Only a
  // zero-page (no results) list hides it. (SHCT-404)
  if (totalPages < 1) return null;

  const go = (next: number) => {
    if (next < 1 || next > totalPages || next === page) return;
    onPageChange(next);
  };

  const range = buildRange(page, totalPages);

  return (
    <div
      className={['pagination', className].filter(Boolean).join(' ')}
      style={{ marginTop: 16 }}
    >
      <button
        type="button"
        className="pagination__btn"
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        <ChevronLeft size={16} />
      </button>
      {range.map((item, idx) =>
        item === 'ellipsis' ? (
          <span key={`e-${idx}`} className="pagination__ellipsis">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            className={`pagination__btn${item === page ? ' pagination__btn--active' : ''}`}
            onClick={() => go(item)}
            aria-current={item === page ? 'page' : undefined}
          >
            {item}
          </button>
        ),
      )}
      <button
        type="button"
        className="pagination__btn"
        onClick={() => go(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
